import React, { useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep, isEqual, omit } from 'lodash';
import { styled } from '@mui/material';
import uuid from 'uuid';

import MobileFormativeSetup from 'components/organisms/Milestones/FormativeSetup/mobile';
import TabletFormativeSetup from 'components/organisms/Milestones/FormativeSetup/tablet';
import DesktopFormativeSetup from 'components/organisms/Milestones/FormativeSetup/desktop';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/TaskManager/PageProvider';
import courseService from 'services/course';
import formativeService from 'services/formative';
import { ResponsiveDialog } from 'shell/components';
import { ACTIVITY_ACTIONS, TYPES } from 'utils/constants';
import { buildPrompt, getChatCompletion, getHTMLWordCount, insertAtIndex, isNatural } from 'utils/helper';

const STEPS = [
    { key: 'context', value: '1. Provide Context' },
    { key: 'baseline', value: '2. Establish a Baseline' },
    { key: 'preview', value: '3. Preview' },
];

const FormativeSetupDialog = () => {
    const { message } = useSnackbar();
    const { course, formatives, setFormatives } = useUser2();
    const { device } = useViewport();
    const {
        setConfirmationModal,
        milestoneDrawer,
        formativeDialog,
        setFormativeDialog,
        formativeForm,
        setFormativeForm,
        formativeErrors,
        setFormativeErrors,
        shouldValidateOnChange,
        setShouldValidateOnChange,
        setAddEditCriteriaDialog,
    } = useContext(PageContext);

    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [hasChanges, setHasChanges] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [isPaused, setIsPaused] = useState(false);

    const formative = useMemo(() => {
        return formatives?.find(({ milestone_id }) => milestone_id === milestoneDrawer.milestoneId);
    }, [formatives, milestoneDrawer.milestoneId]);

    const closeConfirmationModal = () => {
        setConfirmationModal((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
    };

    const onClose = () => {
        if (hasChanges) {
            setConfirmationModal({
                visible: true,
                destructive: true,
                title: 'You Have Unsaved Changes',
                content: 'If you exit without saving, all unsaved changes will be lost.',
                confirmText: 'Exit',
                onConfirm: () => {
                    closeConfirmationModal();
                    setFormativeDialog({ open: false });
                },
                onCancel: closeConfirmationModal,
            });
        } else {
            setFormativeDialog({ open: false });
        }
    };

    const validate = (key) => {
        switch (key) {
            case 'rubric': {
                let hasError = false;
                formativeForm.rubric.forEach((criterion) => {
                    criterion.options.forEach((option) => {
                        const hasOptionError = !isNatural(option.rate);
                        if (hasOptionError) hasError = true;
                        setFormativeErrors((current) => ({
                            ...current,
                            rubric: {
                                ...(current?.rubric || {}),
                                [criterion.id]: {
                                    ...(current?.rubric?.[criterion.id] || {}),
                                    [option.id]: { rate: hasOptionError ? 'Please enter a number' : '' },
                                },
                            },
                        }));
                    });
                });
                return hasError;
            }
            case 'assignmentDescription': {
                let errorMessage = '';
                if (!formativeForm.assignmentDescription) errorMessage = 'Please enter an assignment description';
                if (getHTMLWordCount(formativeForm.assignmentDescription) > 500) errorMessage = 'Word count exceeded';
                setFormativeErrors((current) => ({
                    ...current,
                    assignmentDescription: errorMessage,
                }));
                return Boolean(errorMessage);
            }
            case 'additionalSpecifications': {
                let errorMessage = '';
                if (getHTMLWordCount(formativeForm.additionalSpecifications) > 250) {
                    errorMessage = 'Word count exceeded';
                }
                setFormativeErrors((current) => ({
                    ...current,
                    additionalSpecifications: errorMessage,
                }));
                return Boolean(errorMessage);
            }
            case 'pastAssignment': {
                let errorMessage = '';
                if (getHTMLWordCount(formativeForm.pastAssignment) > 2500) errorMessage = 'Word count exceeded';
                setFormativeErrors((current) => ({
                    ...current,
                    pastAssignment: errorMessage,
                }));
                return Boolean(errorMessage);
            }
            case 'grading': {
                let hasError = false;
                if (getHTMLWordCount(formativeForm.pastAssignment) === 0) {
                    setFormativeErrors((current) => omit(current, ['grading']));
                } else {
                    formativeForm.rubric.forEach((criterion) => {
                        const hasCriterionError = !Boolean(formativeForm.grading[criterion.id]);
                        if (hasCriterionError) hasError = true;
                        setFormativeErrors((current) => ({
                            ...current,
                            grading: {
                                ...(current.grading || {}),
                                [criterion.id]: hasCriterionError,
                            },
                        }));
                    });
                }
                return hasError;
            }
            default:
                break;
        }
    };

    const validateStep = (stepIndex) => {
        switch (stepIndex) {
            case 0: {
                const hasError = [
                    validate('rubric'),
                    validate('assignmentDescription'),
                    validate('additionalSpecifications'),
                ].reduce((acc, cur) => acc || cur, false);
                return hasError;
            }
            case 1: {
                const hasError = [validate('pastAssignment'), validate('grading')].reduce(
                    (acc, cur) => acc || cur,
                    false
                );
                return hasError;
            }
            case 2:
            default:
                break;
        }
    };

    const onClickNext = () => {
        const hasError = validateStep(currentStepIndex);
        setShouldValidateOnChange(hasError);
        if (!hasError) {
            setCurrentStepIndex((current) => current + 1);
            setShouldValidateOnChange(false);
        }
    };

    const onClickBack = () => {
        const hasError = validateStep(currentStepIndex);
        setShouldValidateOnChange(hasError);
        if (!hasError) {
            setCurrentStepIndex((current) => current - 1);
            setShouldValidateOnChange(false);
        }
    };

    const onClickSave = async () => {
        setIsSaveLoading(true);
        if (formative) {
            const updatedFormative = {
                ...formative,
                rubric: formativeForm.rubric,
                rubricSort: formativeForm.rubricSort,
                description: formativeForm.assignmentDescription,
                custom_context: formativeForm.additionalSpecifications,
                baseline: [
                    {
                        sample_submission: formativeForm.pastAssignment,
                        grading: formativeForm.grading,
                    },
                ],
            };
            const response = await formativeService.updateFormative(
                updatedFormative.id,
                omit(updatedFormative, ['_id'])
            );
            setFormatives((current) => current.map((item) => (item.id === formative.id ? response : item)));
            message.success('Changes saved.');
        } else {
            const timestamp = new Date().getTime();
            const newFormative = {
                id: uuid.v4(),
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                milestone_id: milestoneDrawer.milestoneId,
                status: 'draft',
                rubric: formativeForm.rubric,
                rubricSort: formativeForm.rubricSort,
                description: formativeForm.assignmentDescription,
                custom_context: formativeForm.additionalSpecifications,
                baseline: [
                    {
                        sample_submission: formativeForm.pastAssignment,
                        grading: formativeForm.grading,
                    },
                ],
            };
            const response = await courseService.createCourseFormative(course.id, newFormative);
            setFormatives((current) => [...current, response]);
            message.success('Progress saved.');
        }
        setIsSaveLoading(false);
    };

    const onClickComplete = async () => {
        setIsSubmitLoading(true);
        if (formative) {
            const updatedFormative = {
                ...formative,
                status: 'complete',
                rubric: formativeForm.rubric,
                rubricSort: formativeForm.rubricSort,
                description: formativeForm.assignmentDescription,
                custom_context: formativeForm.additionalSpecifications,
                baseline: [
                    {
                        sample_submission: formativeForm.pastAssignment,
                        grading: formativeForm.grading,
                    },
                ],
            };
            const response = await formativeService.updateFormative(
                updatedFormative.id,
                omit(updatedFormative, ['_id'])
            );
            setFormatives((current) => current.map((item) => (item.id === formative.id ? response : item)));
        } else {
            const timestamp = new Date().getTime();
            const formative = {
                id: uuid.v4(),
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                milestone_id: milestoneDrawer.milestoneId,
                status: 'complete',
                rubric: formativeForm.rubric,
                rubricSort: formativeForm.rubricSort,
                description: formativeForm.assignmentDescription,
                custom_context: formativeForm.additionalSpecifications,
                baseline: [
                    {
                        sample_submission: formativeForm.pastAssignment,
                        grading: formativeForm.grading,
                    },
                ],
            };
            const response = await courseService.createCourseFormative(course.id, formative);
            setFormatives((current) => [...current, response]);
        }
        setFormativeDialog({ open: false });
        message.success('Setup complete.');
        setIsSubmitLoading(false);
    };

    const onClickGeneratePreview = () => {
        const handleResponse = async (response) => {
            const reader = response.body.getReader();
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setIsPreviewLoading(false);
                    break;
                }
                const decoded = new TextDecoder('utf-8').decode(value);
                const chunks = decoded.split(',,').filter((chunk) => chunk?.length);
                const parsed = chunks.map((chunk) => JSON.parse(chunk));
                parsed.forEach((parsedChunk) => {
                    const { choices } = parsedChunk;
                    if (choices[0].finish_reason !== 'stop') {
                        const { delta } = choices[0];
                        setPreviewData((current) => {
                            if (!current) return delta.content;
                            return (current += delta.content);
                        });
                    }
                });
            }
        };

        setIsPreviewLoading(true);
        const prompt = buildPrompt(
            '',
            {
                rubric: formativeForm.rubric,
                rubricSort: formativeForm.rubricSort,
                description: formativeForm.assignmentDescription,
                custom_context: formativeForm.additionalSpecifications,
                baseline: [
                    {
                        sample_submission: formativeForm.pastAssignment,
                        grading: formativeForm.grading,
                    },
                ],
            },
            formativeForm.exampleAssignment
        );
        getChatCompletion([{ role: 'user', content: prompt }])
            .then((response) => handleResponse(response))
            .catch((e) => {
                setIsPreviewLoading(false);
                message.error('Something went wrong.');
                console.log(e);
            });
    };

    const togglePause = () => {
        setIsPaused((current) => {
            courseService.createCourseActivity(course.id, {
                course_id: course.id,
                type: TYPES.AI_FEEDBACK,
                action: current ? ACTIVITY_ACTIONS.UNPAUSE : ACTIVITY_ACTIONS.PAUSE,
                data: {
                    formative,
                    displayed: preview,
                    available: previewData,
                },
            });
            return !current;
        });
    };

    const onClickEditContext = () => {
        setCurrentStepIndex(0);
        setPreview(null);
        setPreviewData(null);
    };

    const handleRubricSort = (sort) => {
        setFormativeForm((current) => {
            const newRubric = cloneDeep(current.rubric).map((criterion) => {
                criterion.options.sort((a, b) => {
                    const numA = Number(a.rate);
                    const numB = Number(b.rate);
                    if (a.rate === '') return 1;
                    if (b.rate === '') return -1;
                    if (!numA && !numB) return 0;
                    if (sort === 'asc') return numA - numB;
                    return numB - numA;
                });
                return criterion;
            });
            return { ...current, rubric: newRubric, rubricSort: sort };
        });
    };

    const handleChangeRubric = (criterionId, optionId, name, value) => {
        setFormativeForm((current) => {
            return {
                ...current,
                rubric: current.rubric.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;
                    return {
                        ...criterion,
                        options: criterion.options.map((option) => {
                            if (option.id !== optionId) return option;
                            return {
                                ...option,
                                [name]: value,
                            };
                        }),
                    };
                }),
            };
        });
    };

    const handleClickAddCriterion = () => {
        setAddEditCriteriaDialog({
            open: true,
            criterionId: '',
            title: '',
            description: '',
        });
    };

    const handleClickEditCriterion = (criterionId) => {
        const criterion = formativeForm.rubric.find(({ id }) => id === criterionId);
        setAddEditCriteriaDialog({
            open: true,
            criterionId,
            title: criterion.title,
            description: criterion.description,
        });
    };

    const handleClickAddOption = (criterionId, index) => {
        setFormativeForm((current) => {
            return {
                ...current,
                rubric: current.rubric.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;
                    const newOption = {
                        id: uuid.v4(),
                        title: '',
                        description: '',
                        rate: '',
                    };
                    return {
                        ...criterion,
                        options: insertAtIndex(criterion.options, index, newOption),
                    };
                }),
            };
        });
    };

    const handleClickDeleteOption = (criterionId, optionId) => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Rating?',
            content: 'Are you sure you want to delete this rating option?',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                setFormativeForm((current) => {
                    const newRubric = current.rubric.map((criterion) => {
                        if (criterion.id !== criterionId) return criterion;
                        return {
                            ...criterion,
                            options: criterion.options.filter(({ id }) => id !== optionId),
                        };
                    });
                    const newGrading =
                        current.grading[criterionId] !== optionId
                            ? current.grading
                            : omit(current.grading, [criterionId]);
                    return {
                        ...current,
                        rubric: newRubric,
                        grading: newGrading,
                    };
                });
            },
            onCancel: closeConfirmationModal,
        });
    };

    const handleDragEnd = ({ source, destination, draggableId }) => {
        if (!destination) return;
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setFormativeForm((current) => {
            const newRubric = cloneDeep(current.rubric);
            newRubric.splice(srcIndex, 1);
            newRubric.splice(
                dstIndex,
                0,
                current.rubric.find(({ id }) => id === draggableId)
            );
            return {
                ...current,
                rubric: newRubric,
            };
        });
    };

    const handleChangeAssignmentDescription = (html) => {
        setFormativeForm((current) => ({ ...current, assignmentDescription: html }));
    };

    const handleChangeAdditionalSpecifications = (html) => {
        setFormativeForm((current) => ({ ...current, additionalSpecifications: html }));
    };

    const handleChangePastAssignment = (html) => {
        setFormativeForm((current) => ({ ...current, pastAssignment: html }));
    };

    const handleChangeGrading = (criterionId, optionId) => {
        setFormativeForm((current) => ({
            ...current,
            grading: {
                ...current.grading,
                [criterionId]: optionId,
            },
        }));
    };

    const handleChangeExampleAssignment = (html) => {
        setFormativeForm((current) => ({ ...current, exampleAssignment: html }));
    };

    // always open on first step
    useEffect(() => {
        if (!formativeDialog.open) {
            setCurrentStepIndex(0);
            setPreview(null);
            setPreviewData(null);
            setIsPaused(false);
        }
    }, [formativeDialog.open]);

    // general validation
    useEffect(() => {
        if (shouldValidateOnChange) validateStep(currentStepIndex);
    }, [shouldValidateOnChange, formativeForm, currentStepIndex]);

    // detecting changes
    useEffect(() => {
        const existingFormative = {
            rubric: formative?.rubric,
            rubricSort: formative?.rubricSort,
            description: formative?.description,
            custom_context: formative?.custom_context,
            baseline: formative?.baseline,
        };
        const currentFormative = {
            rubric: formativeForm.rubric,
            rubricSort: formativeForm.rubricSort,
            description: formativeForm.assignmentDescription,
            custom_context: formativeForm.additionalSpecifications,
            baseline: [
                {
                    sample_submission: formativeForm.pastAssignment,
                    grading: formativeForm.grading,
                },
            ],
        };
        setHasChanges(!isEqual(existingFormative, currentFormative));
    }, [formative, formativeForm]);

    // validation auto scroll
    useEffect(() => {
        if (
            formativeErrors.grading &&
            Object.values(formativeErrors?.grading).reduce((acc, cur) => acc || cur, false)
        ) {
            const targetCriterionId = Object.keys(formativeErrors.grading)[0];
            if (targetCriterionId) {
                const targetCriterion = formativeForm.rubric.find((criterion) => criterion.id === targetCriterionId);
                if (!targetCriterion) return;
                const optionId = targetCriterion.options[0].id;
                const optionEl = document.getElementById(optionId);
                if (optionEl) optionEl.scrollIntoView();
            }
        }
    }, [formativeErrors, formativeForm]);

    // pause/play preview generation
    useEffect(() => {
        if (isPaused || previewData === null) return;
        setPreview((current) => {
            const dataNotDisplayed = previewData?.substring(current?.length);
            return `${current || ''}${dataNotDisplayed || ''}`;
        });
    }, [previewData, isPaused]);

    const props = {
        steps: STEPS,
        formative,
        isSubmitLoading,
        isSaveLoading,
        isPreviewLoading,
        currentStepIndex,
        hasChanges,
        preview,
        previewData,
        isPaused,
        togglePause,
        handlers: {
            // step 'context'
            handleRubricSort,
            handleChangeRubric,
            handleClickAddCriterion,
            handleClickEditCriterion,
            handleClickAddOption,
            handleClickDeleteOption,
            handleDragEnd,
            handleChangeAssignmentDescription,
            handleChangeAdditionalSpecifications,
            // step 'baseline'
            handleChangePastAssignment,
            handleChangeGrading,
            // step 'preview'
            handleChangeExampleAssignment,
        },
        onClose,
        onClickNext,
        onClickBack,
        onClickSave,
        onClickComplete,
        onClickGeneratePreview,
        onClickEditContext,
    };

    const renderResponsiveContent = (device) => {
        return {
            mobile: <MobileFormativeSetup {...props} />,
            tablet: <TabletFormativeSetup {...props} />,
            desktop: <DesktopFormativeSetup {...props} />,
        }[device];
    };

    return (
        <CustomResponsiveDialog open={formativeDialog.open} scroll={'paper'} onClose={onClose}>
            {renderResponsiveContent(device)}
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

export default FormativeSetupDialog;
